import { useCallback, useEffect, useMemo } from 'react';
import { useResourceKit, useUrlState } from '@resource-kit/core/react';
import type { PreferenceStore } from '@resource-kit/core';
import type { Condition, ListParams } from '@resource-kit/model';
import {
  serializeConditionsToParams,
  deserializeParamsToConditions,
  isConditionKey,
  INTERNAL_QUERY_KEYS,
  type ColumnTypeMap,
} from '../query/serialize';
import { isWhitelisted, type FilterWhitelist, type SortWhitelist } from '../list/whitelist';

/**
 * The shape is declared as `ListParams` in `@resource-kit/model` since #2057,
 * and this alias is the direction reversed. It used to be the other way round —
 * the package's `ListParams` was an alias of this hook's interface — which made
 * the list CONTRACT a property of the React hook that happens to serialise it
 * into the URL. This name stays because ~dozens of modules import it.
 */
export type DataTableParams = ListParams;

interface UseDataTableParamsConfig {
  page?: number;
  limit?: number;
  sort?: string;
  defaultConditions?: Condition[];
  // Issue #666: when set, table state is also persisted to the preference store
  // under `hgs.dt.<persistKey>` so direct page visits (sidebar nav /
  // tab reopen) restore the last-used filter. URL params always win.
  persistKey?: string;
  // Issue #1231: the resource's backend filter whitelist (same map
  // ResourcePage hands `droppedFilterFields`). When provided, filters
  // restored FROM the preference store is cleaned against it — an entry whose
  // field or op the backend does not accept is dropped and the cleaned
  // set re-persisted, so a stale storage blob can't pin a permanent
  // "filters dropped" banner or a falsely-empty list. Explicit URL params
  // are NOT sanitized (a shared / bookmarked link keeps the existing
  // banner behavior). Resources without a whitelist restore verbatim.
  filterable?: FilterWhitelist;
  // Issue #1639: the resource's backend sort whitelist (the same set
  // the column builders use to disable non-sortable headers).
  // Disabling the headers only stops NEW bad sorts; a sort persisted BEFORE
  // that gate existed (persisted blob, bookmarked / shared URL) still
  // reaches the request and 400s the whole page into an ErrorState with no
  // in-UI recovery. So a sort whose field is outside the whitelist degrades
  // to the default sort at BOTH hydration boundaries (URL read + storage
  // rehydration), mirroring the #1231 filter precedent. Resources without a
  // whitelist (client-paginated lists) restrict nothing.
  sortable?: SortWhitelist;
  // Issue #1642: whether the resource's backend list route implements
  // free-text search. Same defect class as `sortable` above: since the list
  // kernel started 400-ing an unsupported `search`, a needle carried by an
  // old bookmark / shared link / persisted blob hard-errors the page —
  // and because those resources render NO search box, there is no in-UI
  // control left to clear it. So a non-empty `search` is dropped at BOTH
  // hydration boundaries when the resource is not searchable. `undefined`
  // restricts nothing (non-ResourcePage callers); ResourcePage passes the
  // same resolved boolean it hands the table.
  searchable?: boolean;
  // Stale-preset fix: resolve a `_preset` id (URL-persisted presentation
  // state, see INTERNAL_QUERY_KEYS.preset) to its CURRENT conditions.
  // A relative preset ("today"/"tomorrow") persists only its id plus the
  // ABSOLUTE date it resolved to at click time; on a later hydration that
  // date is stale. When this resolver is provided, any hydration path
  // carrying a KNOWN `_preset` re-runs it (fresh date) instead of restoring
  // the persisted absolute conditions, and an UNKNOWN `_preset` is dropped
  // from the URL so no ghost highlight/state lingers. Returns the preset's
  // freshly-built conditions, or `null` when the id matches no known preset.
  // The hook stays preset-agnostic: it depends only on this injected
  // function, never on resource definitions. Callers MUST pass a STABLE
  // (memoized) reference so it can be a hook dependency without churn.
  resolvePresetConditions?: (presetId: string) => Condition[] | null;
  // Issue #2094: the resource's `field-id → column type` map
  // (`deriveColumnTypes(bundle.model.fields)`). Handed to the serializer so a
  // filter bound on an INSTANT column is completed into the canonical
  // `YYYY-MM-DDTHH:MM:SS.sssZ` the list kernel accepts — the filter date
  // pickers emit bare days (#2111), and URLs written before #2111 can still
  // carry `YYYY-MM-DDTHH:mm`; uncompleted, the kernel 400s either of them.
  // Same class as `filterable` / `sortable` directly
  // above: a backend-shaped contract the persistence layer must know so a
  // stored blob or a bookmarked URL cannot hard-error the page. Absent ⇒ no
  // completion (non-ResourcePage callers keep the prior wire form exactly).
  columnTypes?: ColumnTypeMap;
}

// Issue #590: filter / sort / pagination state is persisted in the URL
// search string so the operator's list view survives refresh, share-link,
// and back/forward navigation.
//
// Issue #666: when the URL is empty (direct navigation), state is
// restored from the preference store under `hgs.dt.<persistKey>`. URL params
// always take precedence — storage is only the fallback.
//
// Design:
//   - `useSearchParams` from react-router-dom is the single source of
//     truth (no internal `useState`). Reading derives from URL; writing
//     calls `setSearchParams`.
//   - Read precedence: URL params > preference store > initialConfig.
//   - Hydration from storage fires once on mount (replace: true) when
//     the URL is empty and storage has saved state.
//   - Every URL update persists the normalized params to storage.
//   - Storage is per-resource (keyed by persistKey) so different pages
//     do not interfere with each other.
//   - All storage operations are fail-silent (private browsing / quota).
//   - Default values are OMITTED from the URL on write so a fresh page
//     load (no params) and an operator who selected the defaults
//     round-trip to the same URL — keeps share-links canonical.
//   - `replace: true` on every write so toggling a filter does not
//     bloat history; "back" should leave the page, not step through
//     every filter change.
//   - Page resets to 1 whenever filter/search/limit changes — the same
//     pagination contract the pre-#590 hook honored.
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;
const DEFAULT_SORT = 'created_at:desc';

// True when the URL carries at least one real condition field. Key
// classification is delegated to the serializer's `isConditionKey` so the
// "reserved / `_`-internal" rule has exactly one definition (EQD §1).
// Used to enforce the `_cleared` invariant.
function hasConditionKey(sp: URLSearchParams): boolean {
  for (const k of sp.keys()) {
    if (isConditionKey(k)) return true;
  }
  return false;
}

// ── Persisted table-state helpers (issue #666) ───────────────────────────
//
// The preference store arrives through the resource kit's services seam
// (#2165): this hook says WHICH logical key it wants, and the host decides the
// namespace, the envelope version and whose preferences these are. The physical
// localStorage key is unchanged — the host prefixes `hgs.`, so `dt.<pk>` here
// is still `hgs.dt.<pk>` on disk.
function dtKey(pk: string): string {
  return `dt.${pk}`;
}

function readDtStorage(storage: PreferenceStore, pk: string): URLSearchParams | null {
  // The seam moves `unknown` in both directions on purpose — consumers store
  // different shapes through it — so the string check that used to be the
  // store's `sanitize` lives here, with the only code that knows this entry is
  // a query string.
  const raw = storage.read(dtKey(pk));
  // Present but the wrong shape is CORRUPT, not absent, and returning null
  // alone would leave it in localStorage. `lib/store`'s `sanitize` used to
  // discard it for us; the discard has to move here with the check that
  // decides it. Only when the entry EXISTS — an absent key must not turn a
  // read into a write — and `storage.has` is what says so (#2183). The
  // null-check on the READ that used to stand in for it answered "absent" for a
  // stored `null`, which the host store hands back intact having no
  // sanitizer, so it would leave that entry in place.
  // Order matters only for cost: `raw` is already in hand, so testing its shape
  // first spares a redundant `getItem` for a key that HAS a usable value. A key
  // without one — a first-time operator's included — still pays it, and pays
  // for more than one: `store.ts`'s `read` already issued two `getItem` calls
  // on the way to `null` (effective key, then legacy), so `storage.has` here
  // is a third `getItem` on an absent key, not the second.
  if (typeof raw !== 'string' && storage.has(dtKey(pk))) storage.remove(dtKey(pk));
  const q = typeof raw === 'string' ? raw : null;
  if (!q) return null;
  const sp = new URLSearchParams(q);
  // `page` is a transient cursor, never a persisted preference: a
  // saved page index is meaningless on a later visit once the data
  // set has shifted (issue #666). Strip it on the hydration boundary
  // so neither fresh nor legacy storage blobs restore a stale page.
  sp.delete('page');
  return sp.toString() ? sp : null;
}

function writeDtStorage(storage: PreferenceStore, pk: string, sp: URLSearchParams): void {
  // Mirror the read side: never store `page` (transient cursor), so
  // storage holds only durable preferences (sort / search / filters).
  const toStore = new URLSearchParams(sp);
  toStore.delete('page');
  const q = toStore.toString();
  if (!q) {
    storage.remove(dtKey(pk));
    return;
  }
  storage.write(dtKey(pk), q);
}

// Issue #1639: whether a `<field>:<order>` sort survives the resource's sort
// whitelist. No whitelist means unrestricted (client-paginated resources).
function isSortWhitelisted(sort: string, sortable?: SortWhitelist): boolean {
  if (!sortable) return true;
  return sortable.includes(sort.split(':')[0]);
}

// Issue #1642: whether a non-empty `search` survives the resource's declared
// searchability. `undefined` means unrestricted (no declaration to enforce);
// an EMPTY needle is never dropped — it is the "no search" value.
function isSearchAllowed(search: string, searchable?: boolean): boolean {
  if (search === '') return true;
  return searchable !== false;
}

// Issue #1231 (+ #1639, #1642): strip persisted state the backend would reject.
// Condition keys are checked against the filter whitelist (field + encoded
// op), the reserved `sort` key against the sort whitelist and `search` against
// the resource's searchability; every other reserved / `_`-internal key passes
// through untouched. Returns a NEW params object so the caller can compare it
// to the input to decide whether the storage entry needs rewriting. With no
// declaration at all the input is returned as-is (restore-verbatim).
function sanitizePersistedParams(
  sp: URLSearchParams,
  filterable?: FilterWhitelist,
  sortable?: SortWhitelist,
  searchable?: boolean,
): URLSearchParams {
  if (!filterable && !sortable && searchable === undefined) return sp;
  const cleaned = new URLSearchParams();
  for (const [key, value] of sp.entries()) {
    if (filterable && isConditionKey(key) && !isWhitelisted(key, value, filterable)) continue;
    if (key === 'sort' && !isSortWhitelisted(value, sortable)) continue;
    if (key === 'search' && !isSearchAllowed(value, searchable)) continue;
    cleaned.append(key, value);
  }
  return cleaned;
}

export function useDataTableParams(initialConfig?: UseDataTableParamsConfig) {
  // Scoping preferences to the signed-in caller is the host adapter's job now
  // (#2165), so this hook no longer reads an auth context at all — it names a
  // logical key and the seam decides whose entry that is.
  // `format` is here for ONE member: `timeZone`. The serializer takes the
  // operating zone as a parameter (ADR-0035 D1 / invariant 5), and the seam is
  // where a package reads a host fact. HOW an adapter produces that zone is not
  // this hook's business.
  //
  // ONE call below passes it (#2179): the `params` memo, the only serialize
  // call in this hook that also passes `columnTypes` and can therefore run a
  // completion at all. The URL-writing call takes no options bag members, so it
  // has no zone to be wrong about. The memo caches the serialized RESULT and
  // `format` is a stable host object whose identity never changes — so a zone
  // that changes after mount is not picked up until some other dep moves. That
  // is the known non-self-healing bootstrap window, pinned and explained in
  // `src/lib/__tests__/merchant-tz.test.ts` by
  // `it('answers differently for New York than for Los Angeles')` — the file
  // opens TWO describe blocks named `merchantWallTimeToUtcIso` and this
  // behaviour lives only in the second, "the instant depends on the CONFIGURED
  // zone"; it predates #2167 (the same memo
  // called `getMerchantTz()` inline before) and is NOT to be "fixed" by adding
  // `format.timeZone` to a dependency array here — that is the #2105 axis.
  const { format, storage } = useResourceKit();
  // The router seam (#1801): this hook owns URL-shaped list state but
  // never names a concrete router — the host injects one.
  const { params: searchParams, setParams: setSearchParams } = useUrlState();
  const persistKey = initialConfig?.persistKey;
  const filterable = initialConfig?.filterable;
  const sortable = initialConfig?.sortable;
  const searchable = initialConfig?.searchable;
  const resolvePresetConditions = initialConfig?.resolvePresetConditions;
  const columnTypes = initialConfig?.columnTypes;

  const initialPage = initialConfig?.page ?? DEFAULT_PAGE;
  const initialLimit = initialConfig?.limit ?? DEFAULT_LIMIT;
  const initialSort = initialConfig?.sort ?? DEFAULT_SORT;
  const initialConditions = initialConfig?.defaultConditions ?? [];

  // ─── One-time hydration from the preference store when URL is empty ─────
  // Fires on mount only. If the URL already has any params (shared link,
  // back-nav, bookmark) we leave it untouched so URL remains authoritative.
  // `replace: true` avoids adding a spurious history entry.
  useEffect(() => {
    if (!persistKey) return;
    if (searchParams.toString() !== '') return;
    const stored = readDtStorage(storage, persistKey);
    if (!stored) return;
    // #1231: drop filters the current resource's whitelist no longer
    // accepts BEFORE they reach the URL, and re-persist the cleaned set
    // so the stale entry doesn't resurface on the next visit. Sanitizing
    // is scoped to this storage-hydration path only — explicit URL params
    // keep the existing "banner warns, filter not applied" behavior.
    const cleaned = sanitizePersistedParams(stored, filterable, sortable, searchable);
    if (cleaned.toString() !== stored.toString()) {
      writeDtStorage(storage, persistKey, cleaned);
    }
    if (cleaned.toString() === '') return;
    setSearchParams(() => cleaned, { replace: true });
    // Mount-only by design. CORRECTNESS DEPENDS ON THE CALLER REMOUNTING
    // WHEN persistKey CHANGES: ResourcePage wraps this hook's consumer in
    // `key={definition.slug}` (see ResourcePage.tsx), so switching resources
    // forces a fresh mount and re-runs this hydration. If a future caller
    // reuses one instance across persistKey values, this `[]` would skip
    // re-hydration for the new resource — depend on persistKey there instead.
    // searchParams captured here is the initial empty URL; later URL changes
    // are handled by updateParams. Suppressing exhaustive-deps is intentional.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Derive state from URL (or fall back to initialConfig) ──────────────
  const page = useMemo(() => {
    const raw = searchParams.get('page');
    if (raw === null) return initialPage;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : initialPage;
  }, [searchParams, initialPage]);

  const limit = useMemo(() => {
    const raw = searchParams.get('limit');
    if (raw === null) return initialLimit;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : initialLimit;
  }, [searchParams, initialLimit]);

  const sort = useMemo(() => {
    const raw = searchParams.get('sort');
    if (!raw || !raw.includes(':')) return initialSort;
    // #1639: a URL-carried sort the backend rejects would 400 the whole
    // page, so it degrades to the default instead of being forwarded.
    return isSortWhitelisted(raw, sortable) ? raw : initialSort;
  }, [searchParams, initialSort, sortable]);

  const search = useMemo(() => {
    const raw = searchParams.get('search') ?? '';
    // #1642: a URL-carried needle the backend rejects would 400 the whole
    // page into an ErrorState with no search box left to clear it, so it
    // degrades to "no search" instead of being forwarded.
    return isSearchAllowed(raw, searchable) ? raw : '';
  }, [searchParams, searchable]);

  // Conditions hydrate from URL with a sentinel-based "cleared"
  // marker to distinguish two indistinguishable states:
  //   - "fresh mount, never touched" — empty URL → apply defaults
  //   - "operator clicked 'clear all'" — empty URL → keep empty
  //
  // The sentinel `?_cleared=1` is written by `handleConditionsChange`
  // when called with `[]` AND defaults exist. Without this signal,
  // `onConditionsChange([])` would silently re-apply defaults
  // because the empty URL looked identical to fresh mount.
  //
  // Read precedence:
  //   1. URL has condition keys      → decode them
  //   2. URL has `_cleared=1`        → conditions = [] (operator intent)
  //   3. Empty URL                   → fall back to defaultConditions
  //
  // The `_cleared` flag is auto-erased when the operator adds any
  // condition back (handler writes new condition keys + removes
  // `_cleared` in the same URL transition).
  const conditions = useMemo<Condition[]>(() => {
    // Stale-preset fix: a KNOWN `_preset` in the URL means "these
    // conditions ARE this preset's" — the existing preset-toggle
    // invariant drops `_preset` the moment the operator hand-edits a
    // filter, so if it's still here the URL-decoded conditions are just
    // a stale snapshot of the preset's predicate. Re-run the preset so
    // relative dates ("today"/"tomorrow") are always fresh, discarding
    // the persisted absolute date entirely. This is a render-phase
    // transform (not an effect), so the very first fetch is already
    // fresh — no stale request flashes before a correction lands.
    const presetId = searchParams.get(INTERNAL_QUERY_KEYS.preset);
    if (presetId && resolvePresetConditions) {
      const resolved = resolvePresetConditions(presetId);
      if (resolved) return resolved;
    }
    const decoded = deserializeParamsToConditions(searchParams);
    if (decoded.length > 0) return decoded;
    if (searchParams.get(INTERNAL_QUERY_KEYS.cleared) === '1') return [];
    return initialConditions;
    // initialConditions identity isn't stable across renders in
    // general; callers pass module-level constants where it is.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, resolvePresetConditions]);

  // ─── Writers: mutate URL, never internal state ──────────────────────────
  //
  // Helper that takes a mutator over a draft URLSearchParams and
  // commits it. Always uses `replace: true` to avoid history bloat,
  // and always strips default values so the URL stays canonical.
  const updateParams = useCallback(
    (mutate: (draft: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          // Normalize: drop default values so the canonical
          // URL for "no filter" is exactly the empty search.
          if (next.get('page') === String(DEFAULT_PAGE)) next.delete('page');
          if (next.get('limit') === String(initialLimit)) next.delete('limit');
          if (next.get('sort') === initialSort) next.delete('sort');
          if (next.get('search') === '') next.delete('search');
          // Converge the `_cleared` invariant in ONE place: the
          // sentinel may only exist when there are no real condition
          // keys. Any active condition key here means an active
          // filter, so a stale `_cleared` (e.g. an inconsistent /
          // legacy storage blob hydrated into the URL) must be
          // dropped so it isn't re-persisted as dead weight.
          if (hasConditionKey(next)) next.delete(INTERNAL_QUERY_KEYS.cleared);
          // Persist normalized params so direct re-visits restore
          // them. NOTE: react-router's `setSearchParams(fn)` invokes
          // `fn` exactly once synchronously (it is NOT a React
          // useState reducer — see useSearchParams impl), so this
          // write is not subject to StrictMode double-invocation and
          // runs once per user action. Kept here (not a separate
          // effect) so it never races the mount-time hydration that
          // seeds the URL from this same storage entry.
          if (persistKey) writeDtStorage(storage, persistKey, next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, initialLimit, initialSort, persistKey, storage],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams((p) => {
        if (newPage === DEFAULT_PAGE) p.delete('page');
        else p.set('page', String(newPage));
      });
    },
    [updateParams],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      updateParams((p) => {
        if (newLimit === initialLimit) p.delete('limit');
        else p.set('limit', String(newLimit));
        // Reset page to 1 (= drop the param) when limit changes.
        p.delete('page');
      });
    },
    [updateParams, initialLimit],
  );

  const handleSortChange = useCallback(
    (field: string) => {
      updateParams((p) => {
        const currentSort = p.get('sort') ?? initialSort;
        const [prevField, prevOrder] = currentSort.split(':');
        let nextSort: string;
        if (prevField === field) {
          nextSort = `${field}:${prevOrder === 'asc' ? 'desc' : 'asc'}`;
        } else {
          nextSort = `${field}:asc`;
        }
        if (nextSort === initialSort) p.delete('sort');
        else p.set('sort', nextSort);
      });
    },
    [updateParams, initialSort],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateParams((p) => {
        if (value === '') p.delete('search');
        else p.set('search', value);
        p.delete('page'); // reset to first page
      });
    },
    [updateParams],
  );

  const handleConditionsChange = useCallback(
    // `extras` lets the caller fold page-internal URL state (e.g.
    // `_preset` from ResourcePage) into the SAME URL transition.
    // Necessary because `setSearchParams` is routed through
    // `navigate()` (not React's batched useState), so two
    // synchronous calls would each see the same `prev` snapshot
    // and the second would clobber the first. Each `extras` key
    // with `null` value is deleted; non-null values are set.
    (newConditions: Condition[], extras?: Record<string, string | null>) => {
      updateParams((p) => {
        // Remove every existing non-reserved, non-internal key,
        // then write the new serialized conditions. This is the
        // only safe way to also handle "filter cleared" —
        // partial diff would orphan stale keys in the URL.
        // Keys starting with `_` (e.g. `_preset`) are
        // page-internal presentation state and survive a
        // filter clear unless the caller explicitly overrides
        // via `extras`; `deserializeParamsToConditions` honors
        // the same convention on the read side.
        for (const k of Array.from(p.keys())) {
          if (isConditionKey(k)) p.delete(k);
        }
        // NO `columnTypes` here, deliberately — the URL keeps the value the
        // operator typed, and the instant completion happens once, on the
        // REQUEST (`params` below). The URL is not only a record of the query,
        // it is the editor's seed: `conditions` are decoded straight back out
        // of it, the chip renders from them and `DatePicker` reads their date
        // part. Once the completion became calendar-aware (#2105) an end-edge
        // bound crosses midnight — `to:2026-08-13` is `2026-08-14T06:59:59.999Z`
        // — so writing it back here re-opens the picker on the 14th, and a
        // re-pick of the day it highlights commits the 14th's window. Under the
        // pre-#2105 UTC completion this was invisible: `…T23:59:59.999Z` never
        // crossed a day. Passing the map here would compensate for that only by
        // teaching the display layer to convert instants back, i.e. a second,
        // inverse conversion to undo an eager one.
        //
        // Bookmarks written before this change carry canonical values; those
        // match none of the sub-canonical shapes and pass through the
        // completion untouched, so they keep naming the same window.
        const next = serializeConditionsToParams(newConditions, {});
        for (const [k, v] of Object.entries(next)) {
          p.set(k, v);
        }
        // `_cleared` sentinel: distinguishes "operator
        // explicitly cleared all filters" from "fresh mount,
        // never touched". Set when the operator clears AND
        // defaults are configured (otherwise sentinel adds
        // noise); auto-erased when any condition is added.
        if (newConditions.length === 0 && initialConditions.length > 0) {
          p.set(INTERNAL_QUERY_KEYS.cleared, '1');
        } else {
          p.delete(INTERNAL_QUERY_KEYS.cleared);
        }
        if (extras) {
          for (const [k, v] of Object.entries(extras)) {
            if (v === null) p.delete(k);
            else p.set(k, v);
          }
        }
        p.delete('page'); // reset to first page on filter change
      });
    },
    [updateParams, initialConditions],
  );

  // Stale-preset fix: drop an UNKNOWN `_preset` from the URL. A stale or
  // renamed preset id (from an old bookmark / storage blob) would otherwise
  // linger as dead `?_preset=<id>` state — it highlights no button and the
  // read-side above ignores it for conditions, but leaving it in the URL/
  // storage is exactly the ghost state the fix must not leave behind. Runs
  // on mount AND after hydration seeds the URL (the effect re-fires when
  // `searchParams` changes). A KNOWN preset is left untouched here — its
  // freshness is handled render-side. Deleting the ghost changes the URL →
  // the effect re-fires once, now finds no `_preset`, and settles (no loop).
  useEffect(() => {
    const presetId = searchParams.get(INTERNAL_QUERY_KEYS.preset);
    if (!presetId || !resolvePresetConditions) return;
    if (resolvePresetConditions(presetId) !== null) return; // known: keep
    updateParams((p) => p.delete(INTERNAL_QUERY_KEYS.preset));
  }, [searchParams, resolvePresetConditions, updateParams]);

  // Derived params for API calls
  const params: DataTableParams = useMemo(() => {
    const extraParams =
      conditions.length === 0
        ? undefined
        : columnTypes
          ? serializeConditionsToParams(conditions, { columnTypes, timeZone: format.timeZone })
          : serializeConditionsToParams(conditions, {});
    return {
      page,
      limit,
      sort,
      search: search || undefined,
      extraParams,
    };
  }, [page, limit, sort, search, conditions, columnTypes, format]);

  return {
    page,
    limit,
    sort,
    search,
    conditions,
    params,
    setPage: handlePageChange,
    setLimit: handleLimitChange,
    setSort: handleSortChange,
    setSearch: handleSearchChange,
    onSort: handleSortChange,
    onSearch: handleSearchChange,
    onConditionsChange: handleConditionsChange,
    sortField: sort.split(':')[0],
    // Narrow URL value to the known sort-order union. Any value
    // other than 'asc' falls back to 'desc' — explicit allowlist
    // per EQD §3, no silent any-cast.
    sortOrder: (sort.split(':')[1] === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc',
  };
}
